import { useMutation, useQuery } from '@tanstack/react-query';
import api from '../api';
import { type ApiErrorResponse, type ApiResponse } from '../types';
import { apiDataResponseMapper, apiPayloadMapper } from '../utils';
import {
  type LeadDistanceProductivityDashboardResponse,
  type ProductivityDashboardFiltersPayload,
  type VolumeProductivityDashboardResponse,
  type AddHeapClusterDashboardPayload,
  type CreateDashboardCategoryEntryPayload,
  type CreateProductivityFilesPayload,
  type CreateProductivityFilesResponse,
  type DashboardCategoryLabelsResponse,
  type DashboardDataCategoryEntry,
  type DashboardDataCategoryListResponse,
  type DeleteProductivityFilesPayload,
  type GetDashboardCategoryEntriesPayload,
  type GetDashboardCategoryEntryPayload,
  type HeapClusterDashboardResponse,
  type ProductivityFilesListPayload,
  type ProductivityFilesListResponse,
  type RenameProductivityFilePayload,
  type UpdateDashboardCategoryEntryPayload,
  type UpdateHeapClusterDashboardPayload,
} from './types';

export const getDashboardCategoryLabels = async (): Promise<
  ApiResponse<DashboardCategoryLabelsResponse>
> => {
  const response = await api.get<
    DashboardCategoryLabelsResponse,
    ApiResponse<DashboardCategoryLabelsResponse>
  >('/dashboard/data-category-entries/category-labels/');

  return response;
};

export const getDashboardCategoryEntries = async (
  payload: GetDashboardCategoryEntriesPayload,
): Promise<ApiResponse<DashboardDataCategoryListResponse>> => {
  const response = await api.get<
    DashboardDataCategoryListResponse,
    ApiResponse<DashboardDataCategoryListResponse>
  >('/dashboard/data-category-entries/', {
    params: {
      category: payload.category,
    },
  });

  response.data = apiDataResponseMapper(response.data);

  return response;
};

export const getDashboardCategoryEntry = async (
  payload: GetDashboardCategoryEntryPayload,
): Promise<ApiResponse<DashboardDataCategoryEntry>> => {
  const response = await api.get<
    DashboardDataCategoryEntry,
    ApiResponse<DashboardDataCategoryEntry>
  >(`/dashboard/data-category-entries/${payload.id}/`);

  response.data = apiDataResponseMapper(response.data);

  return response;
};

export const createDashboardCategoryEntry = async (
  payload: CreateDashboardCategoryEntryPayload,
): Promise<ApiResponse> => {
  const mappedPayload = apiPayloadMapper(payload);

  const response = await api.post<
    CreateDashboardCategoryEntryPayload,
    ApiResponse
  >('/dashboard/data-category-entries/', mappedPayload);

  response.data = apiDataResponseMapper(response.data);

  return response;
};

export const updateDashboardCategoryEntry = async (
  payload: UpdateDashboardCategoryEntryPayload,
): Promise<ApiResponse<DashboardDataCategoryEntry>> => {
  const { id, ...rest } = payload;

  const mappedPayload = apiPayloadMapper(rest);

  const response = await api.patch<
    Omit<UpdateDashboardCategoryEntryPayload, 'id'>,
    ApiResponse<DashboardDataCategoryEntry>
  >(`/dashboard/data-category-entries/${id}/`, mappedPayload);

  response.data = apiDataResponseMapper(response.data);

  return response;
};

export const getHeapClusterDashboardConfiguration = async (
  clusterId: string,
): Promise<ApiResponse<HeapClusterDashboardResponse>> => {
  const response = await api.get<
    HeapClusterDashboardResponse,
    ApiResponse<HeapClusterDashboardResponse>
  >(`/iterations/heap-boundary/${clusterId}/dashboard/`);

  response.data = apiDataResponseMapper(response.data);

  return response;
};

export const addHeapClusterToDashboard = async (
  payload: AddHeapClusterDashboardPayload,
): Promise<ApiResponse> => {
  const { id: clusterId, ...rest } = payload;

  const mappedPayload = apiPayloadMapper(rest);

  return await api.post<
    Omit<AddHeapClusterDashboardPayload, 'heapClusterId'>,
    ApiResponse
  >(`/iterations/heap-boundary/${clusterId}/dashboard/`, mappedPayload);
};

export const updateHeapClusterDashboardConfiguration = async (
  payload: UpdateHeapClusterDashboardPayload,
): Promise<ApiResponse> => {
  const { id: clusterId, ...rest } = payload;

  const mappedPayload = apiPayloadMapper(rest);

  return await api.patch<UpdateHeapClusterDashboardPayload, ApiResponse>(
    `/iterations/heap-boundary/${clusterId}/dashboard/`,
    mappedPayload,
  );
};

export const deleteHeapClusterFromDashboard = async (
  clusterId: string,
): Promise<ApiResponse> =>
  await api.delete<void, ApiResponse>(
    `/iterations/heap-boundary/${clusterId}/dashboard/`,
  );

export const getVolumeProductivityDashboard = async (
  payload: ProductivityDashboardFiltersPayload,
): Promise<ApiResponse<VolumeProductivityDashboardResponse>> => {
  const response = await api.get<
    VolumeProductivityDashboardResponse,
    ApiResponse<VolumeProductivityDashboardResponse>
  >('/dashboard/productivity/volume-kpi/', {
    params: apiPayloadMapper(payload),
  });

  response.data = apiDataResponseMapper(response.data);

  return response;
};

export const getLeadDistanceProductivityDashboard = async (
  payload: ProductivityDashboardFiltersPayload,
): Promise<ApiResponse<LeadDistanceProductivityDashboardResponse>> => {
  const response = await api.get<
    LeadDistanceProductivityDashboardResponse,
    ApiResponse<LeadDistanceProductivityDashboardResponse>
  >('/dashboard/productivity/lead-distance/', {
    params: apiPayloadMapper(payload),
  });

  response.data = apiDataResponseMapper(response.data);

  return response;
};

export const exportProductivityDashboard = async (
  payload: ProductivityDashboardFiltersPayload,
): Promise<Blob> => {
  return await api.get('/dashboard/productivity/export/', {
    params: apiPayloadMapper(payload),
    responseType: 'blob',
  });
};

export const getProductivityFilesList = async (
  payload: ProductivityFilesListPayload,
): Promise<ProductivityFilesListResponse> => {
  const response = await api.get('/dashboard/productivity/files/', {
    params: apiPayloadMapper(payload),
  });

  return apiDataResponseMapper(response.data);
};

export const createProductivityFiles = async (
  payload: CreateProductivityFilesPayload,
): Promise<CreateProductivityFilesResponse> => {
  const { duplicateHandling, ...rest } = payload;

  const response = await api.post(
    '/dashboard/productivity/files/bulk/',
    apiPayloadMapper(rest),
    duplicateHandling
      ? { params: { duplicate_handling: duplicateHandling } }
      : undefined,
  );

  return apiDataResponseMapper(response.data);
};

export const deleteProductivityFiles = async (
  payload: DeleteProductivityFilesPayload,
): Promise<ApiResponse> =>
  await api.delete('/dashboard/productivity/files/bulk/', {
    data: apiPayloadMapper(payload),
  });

export const renameProductivityFile = async (
  payload: RenameProductivityFilePayload,
): Promise<ApiResponse> =>
  await api.patch(`/dashboard/productivity/files/${payload.fileId}/`, {
    name: payload.name,
  });

// Hooks.
export const useDashboardCategoryLabels = (enabled = false) =>
  useQuery<ApiResponse<DashboardCategoryLabelsResponse>, ApiErrorResponse>({
    queryKey: ['dashboardCategoryLabels'],
    queryFn: getDashboardCategoryLabels,
    enabled,
  });

export const useDashboardCategoryEntries = (
  payload: GetDashboardCategoryEntriesPayload,
  enabled = false,
) =>
  useQuery<ApiResponse<DashboardDataCategoryListResponse>, ApiErrorResponse>({
    queryKey: ['dashboardCategoryEntries', payload.category],
    queryFn: () => getDashboardCategoryEntries(payload),
    enabled,
  });

export const useDashboardCategoryEntry = (
  payload: GetDashboardCategoryEntryPayload,
) =>
  useQuery<ApiResponse<DashboardDataCategoryEntry>, ApiErrorResponse>({
    queryKey: ['dashboardCategoryEntry', payload.id],
    queryFn: () => getDashboardCategoryEntry(payload),
    enabled: !!payload.id,
  });

export const useCreateDashboardCategoryEntry = () =>
  useMutation<
    ApiResponse<DashboardDataCategoryEntry>,
    ApiErrorResponse,
    CreateDashboardCategoryEntryPayload
  >({
    mutationFn: createDashboardCategoryEntry,
  });

export const useUpdateDashboardCategoryEntry = () =>
  useMutation<
    ApiResponse<DashboardDataCategoryEntry>,
    ApiErrorResponse,
    UpdateDashboardCategoryEntryPayload
  >({
    mutationFn: updateDashboardCategoryEntry,
  });

export const useHeapClusterDashboardConfiguration = (
  clusterId: string,
  enabled = false,
) =>
  useQuery<ApiResponse<HeapClusterDashboardResponse>, ApiErrorResponse>({
    queryKey: ['heapClusterDashboardConfiguration', clusterId],
    queryFn: () => getHeapClusterDashboardConfiguration(clusterId),
    enabled,
  });

export const useAddHeapClusterToDashboard = () =>
  useMutation<ApiResponse, ApiErrorResponse, AddHeapClusterDashboardPayload>({
    mutationFn: addHeapClusterToDashboard,
  });

export const useUpdateHeapClusterDashboardConfiguration = () =>
  useMutation<ApiResponse, ApiErrorResponse, UpdateHeapClusterDashboardPayload>(
    { mutationFn: updateHeapClusterDashboardConfiguration },
  );

export const useDeleteHeapClusterFromDashboard = () =>
  useMutation<ApiResponse, ApiErrorResponse, string>({
    mutationFn: deleteHeapClusterFromDashboard,
  });

export const useVolumeProductivityDashboard = (
  payload: ProductivityDashboardFiltersPayload,
  enabled = false,
) =>
  useQuery<ApiResponse<VolumeProductivityDashboardResponse>, ApiErrorResponse>({
    queryKey: ['volumeProductivityDashboard', payload],
    queryFn: () => getVolumeProductivityDashboard(payload),
    enabled,
  });

export const useLeadDistanceProductivityDashboard = (
  payload: ProductivityDashboardFiltersPayload,
  enabled = false,
) =>
  useQuery<
    ApiResponse<LeadDistanceProductivityDashboardResponse>,
    ApiErrorResponse
  >({
    queryKey: ['leadDistanceProductivityDashboard', payload],
    queryFn: () => getLeadDistanceProductivityDashboard(payload),
    enabled,
  });

export const useExportProductivityDashboard = () =>
  useMutation<Blob, ApiErrorResponse, ProductivityDashboardFiltersPayload>({
    mutationFn: exportProductivityDashboard,
  });

export const useProductivityFilesList = (
  payload: ProductivityFilesListPayload,
  enabled = true,
) =>
  useQuery<ProductivityFilesListResponse, ApiErrorResponse>({
    queryKey: ['productivityFiles', payload],
    queryFn: () => getProductivityFilesList(payload),
    enabled,
  });

export const useCreateProductivityFiles = () =>
  useMutation<
    CreateProductivityFilesResponse,
    ApiErrorResponse,
    CreateProductivityFilesPayload
  >({
    mutationFn: createProductivityFiles,
  });

export const useDeleteProductivityFiles = () =>
  useMutation<ApiResponse, ApiErrorResponse, DeleteProductivityFilesPayload>({
    mutationFn: deleteProductivityFiles,
  });

export const useRenameProductivityFile = () =>
  useMutation<ApiResponse, ApiErrorResponse, RenameProductivityFilePayload>({
    mutationFn: renameProductivityFile,
  });
